/**
 * TenancyAuditCard — Admin Diagnostics (2026-02).
 *
 * READ-ONLY tenancy health map. Hits
 * `GET /api/admin/diagnostics/tenancy-audit` and lays out three
 * sections:
 *   1. Orphaned data — per-collection {total, orphans, has_orphans}
 *   2. Unfiltered read endpoints — static AST scan hits (routes/*.py)
 *   3. Unstamped write paths — insert_one/insert_many without company_id
 *      + a separate "dynamic writes require manual review" bucket
 *
 * No writes. Super-admin only (backend 404s non-super-admins).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Compass, Copy, RefreshCw, AlertTriangle, Search,
  Database, ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatBox({ label, value, tone = 'slate', testId }) {
  const map = {
    slate: 'bg-slate-100 text-slate-900 border-slate-300',
    amber: 'bg-amber-100 text-amber-900 border-amber-300',
    rose: 'bg-rose-100 text-rose-900 border-rose-300',
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  };
  return (
    <div className={`p-3 rounded-lg border ${map[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-mono font-semibold">{value}</div>
    </div>
  );
}

function CollectionRow({ row, i }) {
  const flagged = row.has_orphans;
  return (
    <tr className={`border-t ${flagged ? 'bg-rose-50' : 'hover:bg-slate-50'}`} data-testid={`tenancy-orphan-row-${i}`}>
      <td className="p-2 font-mono text-slate-800">{row.collection}</td>
      <td className="p-2 text-right font-mono">{row.total_rows}</td>
      <td className={`p-2 text-right font-mono ${flagged ? 'text-rose-800 font-semibold' : 'text-slate-500'}`}>
        {row.orphan_rows}
      </td>
      <td className="p-2 text-center">
        {flagged ? (
          <Badge className="bg-rose-100 text-rose-900 border-rose-300">FLAG</Badge>
        ) : (
          <span className="text-emerald-700 text-xs">clean</span>
        )}
      </td>
    </tr>
  );
}

function EndpointRow({ row, i, showsReadsFrom }) {
  return (
    <tr className="border-t hover:bg-slate-50" data-testid={`tenancy-endpoint-row-${i}`}>
      <td className="p-2 font-mono text-[11px]">
        <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-slate-200 text-slate-800">{row.method || 'INSERT'}</span>
        {row.path || `${row.collection}.${row.method}`}
      </td>
      <td className="p-2 font-mono text-[11px]">{row.handler}</td>
      <td className="p-2 font-mono text-[11px]">{row.file}<span className="text-slate-400">:{row.line}</span></td>
      {showsReadsFrom ? (
        <td className="p-2 text-[11px] font-mono text-slate-700">
          {(row.reads_from || []).join(', ')}
        </td>
      ) : (
        <td className="p-2 font-mono text-[11px] text-slate-700">{row.collection}</td>
      )}
    </tr>
  );
}

export default function TenancyAuditCard({ isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/tenancy-audit`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const copyJSON = () => {
    if (!data) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied audit as JSON');
    } catch { /* ignore */ }
  };

  if (!isSuperAdmin) return null;

  const t = data?.totals;
  const anyIssue =
    (t?.collections_with_orphans || 0)
    + (t?.unfiltered_read_endpoints || 0)
    + (t?.unstamped_write_paths || 0);

  const filteredOrphans = (data?.orphaned_data || []).filter((r) =>
    !filter || r.collection.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div
      className="p-5 rounded-xl border border-slate-300 bg-white mb-6"
      data-testid="tenancy-audit-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <Compass className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Tenancy Audit</h3>
          <p className="text-sm text-slate-700">
            Read-only. Maps the entire tenant-scoping surface for the database this pod is bound to:
            which collections have rows filed under no company, which read endpoints don&apos;t filter
            by <code>company_id</code>, and which insert paths don&apos;t stamp it. This is a MAP —
            no fixes are made here.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={run}
            disabled={busy}
            data-testid="tenancy-audit-run-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning…</>
            ) : data ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>
            ) : (
              <><Compass className="w-4 h-4 mr-2" /> Run Tenancy Audit</>
            )}
          </Button>
          {data && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyJSON}
              data-testid="tenancy-audit-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copy JSON
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2" data-testid="tenancy-audit-error">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Audit failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* Plain-language summary */}
          <div
            className={`p-4 rounded-lg border ${
              anyIssue === 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'
            }`}
            data-testid="tenancy-audit-summary"
          >
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className={`w-5 h-5 ${anyIssue === 0 ? 'text-emerald-700' : 'text-amber-700'}`} />
              <span className={`font-semibold text-sm ${anyIssue === 0 ? 'text-emerald-900' : 'text-amber-900'}`}>
                Summary
              </span>
            </div>
            <div className="text-sm text-slate-900" data-testid="tenancy-audit-summary-sentence">
              {data.plain_summary}
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
              <StatBox label="Tenant-scoped collections" value={t.tenant_scoped_collections_detected} tone="slate" testId="stat-tenant-collections" />
              <StatBox label="Collections w/ orphans" value={t.collections_with_orphans} tone={t.collections_with_orphans ? 'rose' : 'emerald'} testId="stat-orphan-collections" />
              <StatBox label="Unfiltered read endpoints" value={t.unfiltered_read_endpoints} tone={t.unfiltered_read_endpoints ? 'amber' : 'emerald'} testId="stat-unfiltered-reads" />
              <StatBox label="Unstamped write paths" value={t.unstamped_write_paths} tone={t.unstamped_write_paths ? 'rose' : 'emerald'} testId="stat-unstamped-writes" />
              <StatBox label="Dynamic writes (review)" value={t.dynamic_write_paths_requiring_review} tone="slate" testId="stat-dynamic-writes" />
            </div>
          </div>

          {/* Section 1 — orphans table */}
          <section data-testid="tenancy-audit-section-orphans">
            <div className="flex items-center justify-between mb-1 gap-2">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                <Database className="w-4 h-4" /> 1. Orphaned data by collection
              </h4>
              <div className="flex items-center gap-1">
                <Search className="w-3.5 h-3.5 text-slate-400" />
                <input
                  className="text-xs border rounded px-2 py-1 w-48"
                  placeholder="filter…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="tenancy-audit-orphan-filter"
                />
              </div>
            </div>
            <div className="border rounded max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left">
                    <th className="p-2">Collection</th>
                    <th className="p-2 text-right">Total rows</th>
                    <th className="p-2 text-right">Orphans</th>
                    <th className="p-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="tenancy-audit-orphan-tbody">
                  {filteredOrphans.map((r, i) => (
                    <CollectionRow key={r.collection} row={r} i={i} />
                  ))}
                  {filteredOrphans.length === 0 && (
                    <tr><td colSpan={4} className="p-4 text-center italic text-slate-500">no matches</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2 — unfiltered read endpoints */}
          <section data-testid="tenancy-audit-section-reads">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">
              2. Read endpoints missing a tenant filter <span className="text-xs text-slate-500 font-normal">(static AST scan of routes/*.py)</span>
            </h4>
            {data.unfiltered_reads.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-2 border rounded bg-emerald-50 border-emerald-200 text-emerald-800">
                clean — every GET handler that reads a tenant-scoped collection references company_id.
              </div>
            ) : (
              <div className="border rounded max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2">Route</th>
                      <th className="p-2">Handler</th>
                      <th className="p-2">File:line</th>
                      <th className="p-2">Reads from</th>
                    </tr>
                  </thead>
                  <tbody data-testid="tenancy-audit-reads-tbody">
                    {data.unfiltered_reads.map((r, i) => (
                      <EndpointRow key={`${r.file}-${r.line}-${r.handler}`} row={r} i={i} showsReadsFrom />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Section 3 — unstamped write paths */}
          <section data-testid="tenancy-audit-section-writes">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">
              3. Insert paths that don&apos;t stamp company_id <span className="text-xs text-slate-500 font-normal">(dict-literal AST scan)</span>
            </h4>
            {data.unstamped_writes.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-2 py-2 border rounded bg-emerald-50 border-emerald-200 text-emerald-800">
                clean — every static insert_one/insert_many payload includes company_id.
              </div>
            ) : (
              <div className="border rounded max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2">Op</th>
                      <th className="p-2">Handler</th>
                      <th className="p-2">File:line</th>
                      <th className="p-2">Collection</th>
                    </tr>
                  </thead>
                  <tbody data-testid="tenancy-audit-writes-tbody">
                    {data.unstamped_writes.map((r, i) => (
                      <EndpointRow key={`${r.file}-${r.line}-${r.handler}-${i}`} row={r} i={i} showsReadsFrom={false} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.dynamic_writes.length > 0 && (
              <details className="mt-2" data-testid="tenancy-audit-dynamic-writes-details">
                <summary className="text-xs text-slate-600 cursor-pointer">
                  + {data.dynamic_writes.length} dynamic insert path(s) needing manual review
                  <span className="text-slate-400 ml-1">(payload passed as a variable — static scan can&apos;t judge)</span>
                </summary>
                <div className="border rounded max-h-64 overflow-auto mt-1">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr className="text-left">
                        <th className="p-2">Op</th>
                        <th className="p-2">Handler</th>
                        <th className="p-2">File:line</th>
                        <th className="p-2">Collection</th>
                      </tr>
                    </thead>
                    <tbody data-testid="tenancy-audit-dynamic-tbody">
                      {data.dynamic_writes.map((r, i) => (
                        <EndpointRow key={`${r.file}-${r.line}-${r.handler}-dyn-${i}`} row={r} i={i} showsReadsFrom={false} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </section>

          {/* Caveats */}
          <section data-testid="tenancy-audit-caveats">
            <details>
              <summary className="text-xs text-slate-500 cursor-pointer">Caveats &amp; scan limits</summary>
              <ul className="mt-1 text-xs text-slate-600 list-disc pl-5 space-y-1">
                {(data.caveats || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </details>
          </section>
        </div>
      )}
    </div>
  );
}
